import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, ArrowRight, Sparkles, BookOpen, GraduationCap, Globe, Zap } from 'lucide-react';
import { getUserSubscription } from '../services/subscriptionService';
import { supabase } from '../services/supabase';
import type { Subscription } from '../types/subscription';

export const CheckoutSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      console.warn('[CheckoutSuccess] No session_id in URL, redirecting to pricing');
      navigate('/pricing');
      return;
    }

    console.log(`[CheckoutSuccess] Session ID: ${sessionId}`);

    // Wait a few seconds for webhook to process
    // This gives Stripe time to send the webhook and our Edge Function to update the DB
    const timer = setTimeout(async () => {
      try {
        console.log('[CheckoutSuccess] Fetching subscription after webhook delay');

        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        const sub = await getUserSubscription(user.id);
        setSubscription(sub);
        console.log('[CheckoutSuccess] Subscription loaded:', sub);
      } catch (error: any) {
        console.error('[CheckoutSuccess] Error fetching subscription:', error);
        setError('Unable to load subscription. Please check your account page.');
      } finally {
        setLoading(false);
      }
    }, 3000); // 3 second delay

    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <div className="text-center max-w-md px-6">
          <div className="relative mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto animate-pulse">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <div className="absolute inset-0 w-20 h-20 mx-auto">
              <div className="w-full h-full border-4 border-blue-200 rounded-full animate-ping opacity-20"></div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-3">
            Activating Your Subscription
          </h2>
          <p className="text-lg text-slate-600 mb-2">
            Setting up your premium features...
          </p>
          <p className="text-sm text-slate-500">
            This usually takes just a few seconds
          </p>

          <div className="mt-8 flex items-center justify-center gap-2">
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-amber-200 overflow-hidden">
          {/* Header with gradient */}
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-8">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <span className="text-4xl">⚠️</span>
            </div>
            <h1 className="text-2xl font-bold text-white text-center">Almost There!</h1>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-slate-700 mb-6 text-center">{error}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-amber-800 text-center">
                Your payment was successful, but we're still processing your subscription.
                Please check your account page in a moment.
              </p>
            </div>

            <div className="flex flex-col gap-3">
              <button
                onClick={() => navigate('/app/account')}
                className="w-full px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Go to Account
              </button>

              <button
                onClick={() => navigate('/app/dashboard')}
                className="w-full px-6 py-3 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors border border-slate-200"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tierName = subscription?.tier === 'essential' ? 'Essential' : 'Pro';
  const isPro = subscription?.tier === 'pro';
  const tierLimit = isPro ? '200' : '60';

  // Feature lists based on tier
  const features = isPro ? [
    { icon: <Zap className="w-5 h-5" />, text: '200 AI lesson plans per month', highlight: true },
    { icon: <Globe className="w-5 h-5" />, text: 'Spanish & world language lesson plans', highlight: false },
    { icon: <GraduationCap className="w-5 h-5" />, text: 'All differentiation modes (intervention, enrichment, SPED)', highlight: false },
    { icon: <BookOpen className="w-5 h-5" />, text: '100% TEKS-aligned content', highlight: false },
  ] : [
    { icon: <Sparkles className="w-5 h-5" />, text: '60 AI lesson plans per month', highlight: true },
    { icon: <Globe className="w-5 h-5" />, text: 'Spanish & world language lesson plans', highlight: false },
    { icon: <GraduationCap className="w-5 h-5" />, text: 'All differentiation modes (intervention, enrichment, SPED)', highlight: false },
    { icon: <BookOpen className="w-5 h-5" />, text: '100% TEKS-aligned content', highlight: false },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-2xl w-full">
        {/* Success Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200">
          {/* Header with gradient */}
          <div className={`bg-gradient-to-r ${
            isPro
              ? 'from-indigo-600 to-purple-600'
              : 'from-blue-600 to-indigo-600'
          } px-8 py-10 text-center relative overflow-hidden`}>
            {/* Decorative elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full -mr-20 -mt-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-5 rounded-full -ml-16 -mb-16"></div>

            {/* Success Icon */}
            <div className="relative w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl animate-in zoom-in duration-500">
              <CheckCircle2 className="w-12 h-12 text-green-600" strokeWidth={2.5} />
            </div>

            {/* Heading */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Welcome to {tierName}! 🎉
            </h1>

            <p className="text-lg text-blue-100 max-w-md mx-auto">
              Your subscription is now active. You have full access to all {tierName} features.
            </p>

            {/* Status badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 backdrop-blur-sm rounded-full border border-white border-opacity-30">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-white">Active Subscription</span>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Feature Highlight */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className={`w-5 h-5 ${isPro ? 'text-indigo-600' : 'text-blue-600'}`} />
                <h3 className="text-xl font-bold text-slate-900">What's Included</h3>
              </div>

              <div className="grid gap-3">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-3 p-4 rounded-lg transition-all ${
                      feature.highlight
                        ? isPro
                          ? 'bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200'
                          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200'
                        : 'bg-slate-50 border border-slate-200'
                    }`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${
                      feature.highlight
                        ? isPro ? 'text-indigo-600' : 'text-blue-600'
                        : 'text-green-600'
                    }`}>
                      {feature.highlight ? feature.icon : <CheckCircle2 className="w-5 h-5" />}
                    </div>
                    <span className={`${
                      feature.highlight
                        ? 'font-semibold text-slate-900'
                        : 'font-medium text-slate-700'
                    }`}>
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 rounded-xl p-6 mb-6">
              <h3 className="text-lg font-bold text-slate-900 mb-3">🚀 Ready to Get Started?</h3>
              <p className="text-sm text-slate-600 mb-4">
                Create your first {tierName} lesson plan in minutes.
                Choose from all subjects, grade levels, and differentiation modes.
              </p>
              <div className="flex items-center gap-2 text-sm">
                <div className="flex -space-x-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    K
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    5
                  </div>
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-teal-500 border-2 border-white flex items-center justify-center text-white text-xs font-bold">
                    12
                  </div>
                </div>
                <span className="text-slate-600 font-medium">All grades. All subjects. Instantly.</span>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              <button
                onClick={() => navigate('/app/create')}
                className={`flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r ${
                  isPro
                    ? 'from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                    : 'from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                } text-white font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl transform hover:scale-105`}
              >
                Start Creating Lessons
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={() => navigate('/app/dashboard')}
                className="flex items-center justify-center gap-2 px-6 py-4 bg-slate-100 text-slate-700 font-semibold rounded-lg hover:bg-slate-200 transition-colors border border-slate-300"
              >
                <BookOpen className="w-5 h-5" />
                View Dashboard
              </button>
            </div>

            <button
              onClick={() => navigate('/app/account')}
              className="w-full px-6 py-3 text-slate-600 hover:text-slate-900 font-medium rounded-lg hover:bg-slate-50 transition-colors text-sm"
            >
              View Account Settings →
            </button>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-slate-50 border-t border-slate-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-slate-600">
                <span className="font-semibold text-slate-900">Need help?</span> We're here for you.
              </div>
              <a
                href="mailto:pablo@lessonlyai.com"
                className={`inline-flex items-center gap-2 px-4 py-2 ${
                  isPro
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                } text-white text-sm font-semibold rounded-lg transition-all shadow-md hover:shadow-lg`}
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>

        {/* Additional Info Card */}
        <div className="mt-6 bg-white bg-opacity-80 backdrop-blur-sm rounded-xl border border-slate-200 p-6 shadow-lg">
          <p className="text-sm text-slate-600 text-center">
            <span className="font-semibold text-slate-900">Receipt sent!</span> Check your email for your purchase confirmation and invoice.
          </p>
        </div>
      </div>
    </div>
  );
};
